<template>
  <div :class="isPC ? 'mt-[35px]' : 'mt-[40px]'">
    <div
      v-for="option in options"
      :key="option.code"
      class="relative flex w-full items-center overflow-hidden last:mb-0 cursor-pointer"
      :class="isPC ? 'mb-[14px] rounded-[20px]' : 'mb-[16px] rounded-[16px]'"
      :style="rowBgStyle(option)"
      @click="emit('select', option)"
    >
      <!-- 未开始：纯选项，无进度条 -->
      <span
        v-if="!showPercent"
        class="font-medium text-[#121212]"
        :class="isPC ? 'px-[20px] py-[21px] text-[24px] leading-[27px]' : 'px-[24px] py-[12px] text-[28px]'"
      >
        {{ $label(option.label) }}
      </span>

      <!-- 进行中/已截止/已结束：占比进度条 + 百分比 -->
      <template v-else>
        <!-- 进度条背景层 -->
        <div
          class="absolute inset-y-0 left-0"
          :class="isPC ? 'rounded-r-[20px]' : 'rounded-r-[16px]'"
          :style="fillStyle(option)"
        />
        <!-- 内容层 -->
        <div
          class="relative flex w-full items-center"
          :class="isPC ? 'px-[20px] py-[21px]' : 'px-[16px] py-[20px]'"
        >
          <span
            class="min-w-0 break-words font-medium text-[#121212]"
            :class="isPC ? 'text-[24px] leading-[27px]' : 'text-[28px] leading-[32px]'"
          >
            {{ $label(option.label) }}
          </span>
          <img
            v-if="modeOf(option) === 'winner'"
            src="../images/icon-correct.png"
            :class="isPC ? 'ml-[8px] h-[34px] w-[34px] shrink-0' : 'ml-[12px] h-[32px] w-[32px] shrink-0'"
          />
          <span
            class="ml-auto shrink-0 pl-[8px] text-[#121212]"
            :class="isPC ? 'text-[14px]' : 'text-[24px]'"
          >
            {{ option.percent || 0 }}%
          </span>
        </div>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import { useLocaleString, useFegoPageInfo } from '@ark/web-shared/use';
import type { PredictOption } from '../../shared/types';
/**
 * 选项渲染模式：
 * - default：白底 + 灰色进度条
 * - selected：整行浅橙 #FFD2B2
 * - winner：整行深橙 #FF6900 + 白字 + 勾选 icon
 */
type OptionMode = 'default' | 'winner' | 'selected';
const __sfc_main = {};
__sfc_main.props = {
  options: {
    key: "options",
    required: false,
    type: Array,
    default: () => []
  },
  showPercent: {
    key: "showPercent",
    required: false,
    type: Boolean,
    default: false
  },
  revealed: {
    key: "revealed",
    required: false,
    type: Boolean,
    default: false
  },
  selectedCode: {
    key: "selectedCode",
    required: false,
    type: [String, null],
    default: null
  },
  winnerCode: {
    key: "winnerCode",
    required: false,
    type: [String, null],
    default: null
  }
};
__sfc_main.setup = (__props, __ctx) => {
  const $label = useLocaleString();
  const {
    isPC
  } = useFegoPageInfo();
  const props = __props;
  const emit = __ctx.emit;

  /**
   * 判断选项的渲染模式，优先级：winner > selected > default
   * - 已揭晓 + 匹配 winnerCode → winner
   * - 匹配 selectedCode → selected（用户选中）
   * - 其余 → default
   */
  function modeOf(option: PredictOption): OptionMode {
    if (props.revealed && option.code === props.winnerCode) return 'winner';
    if (option.code === props.selectedCode) return 'selected';
    return 'default';
  }
  const ROW_BG: Record<OptionMode, string> = {
    winner: '#FFF',
    selected: '#FFF',
    default: '#FFF'
  };
  const FILL_BG: Record<OptionMode, string> = {
    winner: '#FF6900',
    selected: '#FFD2B2',
    default: '#EBEBEB'
  };
  function rowBgStyle(option: PredictOption): Record<string, string> {
    const mode = modeOf(option);
    return {
      backgroundColor: ROW_BG[mode],
      ...(mode === 'winner' ? {
        border: '1px solid #FF6900'
      } : {})
    };
  }
  function fillStyle(option: PredictOption): Record<string, string> {
    return {
      backgroundColor: FILL_BG[modeOf(option)],
      width: (option.percent || 0) + '%'
    };
  }
  return {
    $label,
    isPC,
    emit,
    modeOf,
    rowBgStyle,
    fillStyle
  };
};
export default __sfc_main;
</script>
